/**
 * @bos/portal3 — THE TAB ITSELF, as a signal (prior review B3/p1/p2).
 *
 * The stated history: owners missed halted doors for hours. From another tab the portal
 * was pixel-identical whether the machine was building, waiting for a person, or finished
 * — a static "Sorvel Portal" title and no favicon at all (a 404 on every load).
 *
 * So the tab now says two things without being opened: WHICH room of WHICH build is in
 * there, and — louder than anything else — whether the machine is waiting. The waiting
 * favicon is a different colour and a different shape (brass, a pause), not a badge on the
 * same mark: a person scanning a strip of tabs reads shape and colour, not detail.
 *
 * Both icons are inline SVG data URIs built from the design tokens — no asset, no request,
 * nothing to 404. `index.html` carries the resting one so every page has an icon; this
 * module only ever swaps the `href` and always puts it back on the way out.
 */
import { useEffect } from 'react';

const PINE = '#1e5b45';
const BRASS = '#8a6d2e';

/** The resting seal: the brand's pine tile with its serif S — the header mark, shrunk. */
const SEAL_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
  `<rect width="32" height="32" rx="8" fill="${PINE}"/>` +
  `<text x="16" y="23.5" text-anchor="middle" font-family="Georgia,'Times New Roman',serif"` +
  ` font-size="21" font-weight="700" fill="#ffffff">S</text></svg>`;

/** The waiting seal: brass, and a pause — the machine has stopped, on purpose, for you. */
const WAITING_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
  `<rect width="32" height="32" rx="8" fill="${BRASS}"/>` +
  `<rect x="9.5" y="8.5" width="5" height="15" rx="1.6" fill="#ffffff"/>` +
  `<rect x="17.5" y="8.5" width="5" height="15" rx="1.6" fill="#ffffff"/></svg>`;

export type FaviconVariant = 'resting' | 'waiting';

/** The icon as a data URI. Exported so the co-located test can assert the two differ. */
export function faviconDataUri(variant: FaviconVariant): string {
  const svg = variant === 'waiting' ? WAITING_SVG : SEAL_SVG;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** The title every page outside a run wears — what this module restores on the way out. */
export const DEFAULT_TITLE = 'Sorvel Portal';

/** Point the document's icon at one of the two seals, creating the link if the page has
 *  none (a test DOM, or an index.html that predates the icon). */
export function applyFavicon(variant: FaviconVariant): void {
  if (typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>('link#favicon');
  if (link === null) {
    link = document.createElement('link');
    link.id = 'favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/svg+xml';
  link.href = faviconDataUri(variant);
}

/**
 * Wear a title and an icon for as long as this component is mounted, and put both back
 * when it unmounts — leaving a stale "⏸ Waiting for you" on the dashboard would be the
 * same lie in the other direction.
 */
export function useRunHead(title: string, waiting: boolean): void {
  useEffect(() => {
    document.title = title;
    applyFavicon(waiting ? 'waiting' : 'resting');
    return () => {
      document.title = DEFAULT_TITLE;
      applyFavicon('resting');
    };
  }, [title, waiting]);
}
